<template>
  <v-card :color="$vuetify.theme.dark ? '#131c29' : '#E0EAF2'">
    <v-row>
      <v-col cols="12" sm="12">
        <v-card class="pa-3 rounded-0 elevation-0 mt-6" :color="$vuetify.theme.dark ? '#172233' : '#E8F1F8'">
          <v-sheet 
            class="transactionTable liveTradeTable walletTable pb-3"
            :color="$vuetify.theme.dark ? '#1A2A3E' : '#F1F7FB'"
          >
            <v-data-table 
              :headers="headers" 
              :items="traderTransactions" 
              :search="search" 
              :items-per-page="6"
              :loading="loading"
            >
              <!-- Transaction Type Column -->
              <template v-slot:item.trx_type="{ item }">
                <div class="d-flex align-center">
                  <v-icon small :class="getTransactionIcon(item.trx_type).class">
                    {{ getTransactionIcon(item.trx_type).icon }}
                  </v-icon>
                  <span class="ml-1">{{ formatTransactionType(item.trx_type) }}</span>
                </div>
              </template>

              <!-- Date Column -->
              <template v-slot:item.created_at="{ item }">
                {{ formatMySQLDate(item.created_at) }}
              </template>

              <!-- Amount Column -->
              <template v-slot:item.amount="{ item }">
                {{ formatAmount(item.amount) }}
              </template>

              <!-- Account Number Column -->
              <template v-slot:item.acc_no="{ item }">
                {{ formatAccountNumber(item) }}
              </template>

              <!-- Transaction Fee Column -->
              <template v-slot:item.transaction_fee="{ item }">
                {{ formatTransactionFee(item.transaction_fee) }}
              </template>

              <!-- Net Amount Column -->
              <template v-slot:item.net_amount="{ item }">
                {{ formatNetAmount(item.net_amount) }}
              </template>

              <!-- Status Column -->
              <template v-slot:item.status="{ item }">
                <div class="d-flex align-center">
                  <v-icon small :class="getStatusIcon(item.status).class">
                    {{ getStatusIcon(item.status).icon }}
                  </v-icon>
                  <span class="ml-1">{{ item.status }}</span>
                </div>
              </template>
            </v-data-table>
          </v-sheet>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { TRANSACTION__ACTIONS } from '@/store/action-types';

export default {
  name: 'TraderTransactionsTable',

  data() {
    return {
      search: "",
      loading: false,
      headers: [
        {
          text: "Transaction Type",
          align: "start",
          value: "trx_type",
          sortable: true
        },
        { 
          text: "Date & Time", 
          value: "created_at",
          sortable: true 
        },
        { 
          text: "Amount", 
          value: "amount",
          align: "right",
          sortable: true
        },
        { 
          text: "Account No.", 
          value: "acc_no",
          sortable: false 
        },
        { 
          text: "Transaction Fee", 
          value: "transaction_fee",
          align: "right",
          sortable: true
        },
        { 
          text: "Net Amount", 
          value: "net_amount",
          align: "right",
          sortable: true
        },
        { 
          text: "Status", 
          value: "status",
          sortable: true 
        }
      ],
      date: [this.getDefaultStartDate(), this.getCurrentDate()],
      modal: false,
    };
  },

  computed: {
    ...mapState("transactions", ["traderTransactions"]),
    
    dateRangeText() {
      return this.date.join(" ~ ");
    },
  },

  methods: {
    ...mapActions("transactions", [TRANSACTION__ACTIONS.TRADERTRANSACTIONS]),

    formatMySQLDate(mysqlDate) {
      if (!mysqlDate) return "Invalid date";

      try {
        const isoFormattedDate = mysqlDate.replace('T', ' ').split('.')[0];
        const [datePart, timePart] = isoFormattedDate.split(' ');
        const [year, month, day] = datePart.split('-');
        const [hour, minute] = timePart.split(':');

        const hour12 = hour % 12 || 12;
        const amPm = hour >= 12 ? 'pm' : 'am';

        return `${day}.${month}.${year} - ${hour12}:${minute} ${amPm}`;
      } catch (error) {
        console.error('Date formatting error:', error);
        return "Invalid date";
      }
    },

    formatAmount(amount) {
      return amount?.toFixed(2) ?? "0.00";
    },

    formatTransactionFee(fee) {
      if (fee === 0) return "0.00";
      if (!fee) return "N/A";
      return fee.toFixed(2);
    },

    formatNetAmount(amount) {
      if (amount === 0) return "0.00";
      if (!amount) return "N/A";
      return amount.toFixed(2);
    },

    formatAccountNumber(item) {
      if (item.from_account && item.acc_no) {
        return `From: ${item.from_account} To: ${item.acc_no}`;
      }
      return item.acc_no || "N/A";
    },

    formatTransactionType(type) {
      return type || "Internal Transfer";
    },

    getTransactionIcon(type) {
      const baseClass = "mr-1 text-gray";
      switch (type) {
        case "Deposit":
          return { icon: "mdi-tray-arrow-down", class: `mdi ${baseClass}` };
        case "Withdrawal":
          return { icon: "mdi-tray-arrow-up", class: `mdi ${baseClass}` };
        default:
          return { icon: "mdi-swap-horizontal", class: `mdi ${baseClass}` };
      }
    },

    getStatusIcon(status) {
      switch (status) {
        case "Approved":
          return { icon: "mdi-check", class: "mdi text--green mr-1" };
        case "Rejected":
          return { icon: "mdi-window-close", class: "mdi text-red mr-1" };
        default:
          return { icon: "mdi-refresh", class: "mdi text-warning mr-1" };
      }
    },

    getCurrentDate() {
      return new Date().toISOString().split('T')[0];
    },

    getDefaultStartDate() {
      return "2022-01-01";
    },

    async fetchTransactions() {
      try {
        this.loading = true;
        await this[TRANSACTION__ACTIONS.TRADERTRANSACTIONS](this.$route.query.id);
      } catch (error) {
        console.error('Failed to fetch transactions:', error);
      } finally {
        this.loading = false;
      }
    },
  },

  async mounted() {
    await this.fetchTransactions();
  },
};
</script>